import { useCallback, useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatSAR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, XCircle, RefreshCw, Ban, Clock, Edit2, Trash2, PlusCircle, Banknote, CreditCard } from "lucide-react";
import CancelTransactionModal from "@/components/CancelTransactionModal";
import EditTransactionModal from "@/components/EditTransactionModal";
import AddServiceModal from "@/components/AddServiceModal";

/**
 * Lists today's transactions with cancel/change actions.
 * Props:
 *   refreshKey  — change to force reload (e.g. after a new tx is saved)
 *   onChangeStart(tx)  — fired when user chose "Change service"; parent should
 *                       prefill the form with tx.customer_phone for a new tx.
 */
export default function TodaysTransactions({ refreshKey, onChangeStart }) {
  const { t } = useLang();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "owner";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [modalTx, setModalTx] = useState(null);
  const [modalMode, setModalMode] = useState("cancel");
  const [editing, setEditing] = useState(null);
  const [addingTo, setAddingTo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions/today");
      setList(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  function openCancel(tx) { setModalTx(tx); setModalMode("cancel"); }
  function openAddService(tx) { setAddingTo(tx); }

  async function deleteTx(tx) {
    if (!window.confirm(t("tx.deleteConfirm"))) return;
    try {
      await api.delete(`/transactions/${tx.id}`);
      toast.success(t("tx.deleteSuccess"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }

  function afterDone(refund, mode) {
    setModalTx(null);
    load();
    if (mode === "change" && refund) {
      onChangeStart?.({ customer_phone: refund.customer_phone });
    }
  }

  const activeCount = list.filter((t) => !t.cancelled).length;
  const cancelledCount = list.length - activeCount;

  return (
    <div className="card-luxury accent-leaf">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(!expanded); } }}
        data-testid="today-tx-toggle"
        className="w-full p-4 flex items-center justify-between gap-3 cursor-pointer select-none"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-leaf/15 text-leaf grid place-items-center">
            <Clock className="h-5 w-5" />
          </div>
          <div className="text-start">
            <div className="text-xs uppercase tracking-[0.2em] text-leaf">{t("cancel.todayTxTitle")}</div>
            <div className="text-sm text-foreground/60">
              {activeCount} aktif
              {cancelledCount > 0 && <span className="text-red-400"> · {cancelledCount} dibatalkan</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="p-2 rounded-full hover:bg-leaf/10 text-leaf"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {expanded ? <ChevronUp className="h-5 w-5 text-foreground/50" /> : <ChevronDown className="h-5 w-5 text-foreground/50" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gold/15 p-3 space-y-2 max-h-[460px] overflow-y-auto scrollbar-thin">
          {loading && <div className="text-center py-6 text-foreground/40 text-sm">{t("common.loading")}</div>}
          {!loading && list.length === 0 && (
            <div className="text-center py-8 text-foreground/40 text-sm">{t("cancel.empty")}</div>
          )}
          {list.map((tx) => (
            <div
              key={tx.id}
              data-testid={`today-tx-${tx.id}`}
              className={`bg-charcoal-900/50 border rounded-lg p-3 ${
                tx.cancelled ? "border-red-500/30 opacity-70" : "border-gold/15"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground/50">{formatDate(tx.created_at).split(",").pop().trim()}</span>
                    <span className="text-sm font-medium">{tx.customer_phone}</span>
                    {(() => {
                      const m = tx.payment_method || "cash";
                      if (m === "split") return (
                        <span className="text-[10px] bg-gradient-to-r from-leaf/20 to-gold/20 text-gold uppercase rounded-full px-2 py-0.5 inline-flex items-center gap-1 border border-gold/30">
                          <Banknote className="h-3 w-3" /><CreditCard className="h-3 w-3" /> Split
                        </span>
                      );
                      if (m === "card") return (
                        <span className="text-[10px] bg-gold/15 text-gold uppercase rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <CreditCard className="h-3 w-3" /> {t("kasir.payCard")}
                        </span>
                      );
                      return (
                        <span className="text-[10px] bg-leaf/15 text-leaf uppercase rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Banknote className="h-3 w-3" /> {t("kasir.payCash")}
                        </span>
                      );
                    })()}
                    {tx.cancelled && (
                      <span className="text-[10px] bg-red-500/20 text-red-300 uppercase rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Ban className="h-3 w-3" /> {t("cancel.cancelled")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-foreground/60 mt-1 truncate">
                    {tx.items.map((i) => i.service_name_id).join(" · ")}
                  </div>
                  {tx.cancel_reason && (
                    <div className="mt-1 text-[11px] text-red-300/80 italic">
                      ⓘ {tx.cancel_reason}{tx.cancel_notes && ` — ${tx.cancel_notes}`}
                    </div>
                  )}
                </div>
                <div className="text-end shrink-0">
                  <div className={`text-base font-heading ${tx.cancelled ? "line-through text-foreground/40" : "text-gold"}`}>
                    {formatSAR(tx.total)}
                  </div>
                </div>
              </div>

              {!tx.cancelled && (
                <div className="flex gap-2 mt-2 pt-2 border-t border-gold/10 flex-wrap">
                  <button
                    onClick={() => openAddService(tx)}
                    data-testid={`addsvc-btn-${tx.id}`}
                    className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-full border border-leaf/40 text-leaf hover:bg-leaf/10 transition"
                  >
                    <PlusCircle className="h-3.5 w-3.5" /> {t("addsvc.btn")}
                  </button>
                  <button
                    onClick={() => openCancel(tx)}
                    data-testid={`cancel-btn-${tx.id}`}
                    className="flex-1 min-w-[100px] inline-flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 transition"
                  >
                    <XCircle className="h-3.5 w-3.5" /> {t("cancel.btn")}
                  </button>
                  {canManage && (
                    <>
                      <button
                        onClick={() => setEditing(tx)}
                        data-testid={`edit-btn-${tx.id}`}
                        className="inline-flex items-center justify-center gap-1.5 text-xs py-1.5 px-3 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition"
                        title={t("tx.edit")}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteTx(tx)}
                        data-testid={`delete-btn-${tx.id}`}
                        className="inline-flex items-center justify-center gap-1.5 text-xs py-1.5 px-3 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 transition"
                        title={t("tx.delete")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
              {tx.cancelled && canManage && (
                <div className="mt-2 pt-2 border-t border-gold/10">
                  <button
                    onClick={() => deleteTx(tx)}
                    className="w-full inline-flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("tx.delete")}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalTx && (
        <CancelTransactionModal
          tx={modalTx}
          mode={modalMode}
          onClose={() => setModalTx(null)}
          onDone={afterDone}
        />
      )}

      {editing && (
        <EditTransactionModal
          tx={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {addingTo && (
        <AddServiceModal
          tx={addingTo}
          onClose={() => setAddingTo(null)}
          onDone={() => { setAddingTo(null); load(); }}
        />
      )}
    </div>
  );
}
